import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import Button from '../components/Button';
import { Icon } from '../components/icons';
import type { Project } from '../types';

type ModalMode = { kind: 'create' } | { kind: 'edit'; project: Project } | null;

export default function ProjectsPage() {
  const { instance } = useMsal();
  const cfg = useRuntimeConfig();
  const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { activeProjectId, setActiveProjectId, refreshProjects: refreshActiveContext } = useProject();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState<ModalMode>(null);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setProjects(await api.getProjects(true));
    } catch (e: any) {
      showToast(e.message ?? 'Failed to load projects.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setName(''); setDomain(''); setDescription('');
    setModal({ kind: 'create' });
  }

  function openEdit(project: Project) {
    setName(project.name); setDomain(project.domain ?? ''); setDescription(project.description ?? '');
    setModal({ kind: 'edit', project });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!modal || !name.trim()) return;
    setSaving(true);
    try {
      // Send trimmed values as-is (even if empty) rather than converting to undefined —
      // omitted fields mean "no change" server-side, so that would make it impossible to
      // clear a previously-set domain/description back to blank when editing.
      if (modal.kind === 'create') {
        const created = await api.createProject({ name: name.trim(), domain: domain.trim(), description: description.trim() });
        showToast(`Project "${created.name}" created.`, 'success');
      } else {
        await api.updateProject(modal.project.id, { name: name.trim(), domain: domain.trim(), description: description.trim() });
        showToast('Project updated.', 'success');
      }
      setModal(null);
      await load();
      await refreshActiveContext();
    } catch (e: any) {
      showToast(e.message ?? 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleArchive(project: Project) {
    const archiving = project.status === 'active';
    if (archiving) {
      const ok = await confirm(
        `Archive "${project.name}"? It will stop appearing in the project switcher, but its context and history are kept.`,
        { title: 'Archive project', confirmLabel: 'Archive', danger: true },
      );
      if (!ok) return;
    }
    setBusyId(project.id);
    try {
      await api.updateProject(project.id, { status: archiving ? 'archived' : 'active' });
      showToast(archiving ? 'Project archived.' : 'Project restored.', 'success');
      await load();
      await refreshActiveContext();
    } catch (e: any) {
      showToast(e.message ?? 'Failed to update project.', 'error');
    } finally {
      setBusyId(null);
    }
  }

  const visibleProjects = projects.filter(p => showArchived || p.status === 'active');

  if (loading) return <div className="page-loading"><span className="spinner" />Loading projects…</div>;

  return (
    <div className="page">
      <Link to="/settings" className="settings-back-link">← Organisation Settings</Link>

      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">
            Give the AI generator durable knowledge of what this project/product is, so test cases
            and generated code are grounded in real context instead of just the immediate prompt.
          </p>
        </div>
        <Button onClick={openCreate}>+ New Project</Button>
      </div>

      <div className="settings-card">
        <div className="row-flex" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <label className="row-flex" style={{ fontSize: 13, color: 'var(--t2)', gap: '0.4rem' }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show archived
          </label>
          <button className="btn btn-outline btn-sm" onClick={load}>Refresh</button>
        </div>

        {visibleProjects.length === 0 ? (
          <p style={{ color: 'var(--t2)', textAlign: 'center', padding: '2rem 0' }}>
            No projects yet. Create one to start capturing project context for AI generation.
          </p>
        ) : (
          <div className="users-table-wrapper">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleProjects.map(p => {
                  const isActive = p.id === activeProjectId;
                  const isBusy = busyId === p.id;
                  return (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/settings/projects/${p.id}`} style={{ fontWeight: 500 }}>{p.name}</Link>
                        {p.description && <div className="hint-text">{p.description}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--t2)' }}>{p.domain || '—'}</td>
                      <td>
                        <span className={`badge ${p.status === 'active' ? 'badge-approved' : 'badge-review'}`}>
                          {p.status === 'active' ? 'Active' : 'Archived'}
                        </span>
                        {isActive && <span className="you-badge" style={{ marginLeft: '0.4rem' }}>In use</span>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--t2)' }}>
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {p.status === 'active' && (
                            <button
                              className="btn btn-sm btn-outline"
                              disabled={isActive}
                              onClick={() => setActiveProjectId(p.id)}
                            >
                              {isActive ? 'Active' : 'Use this project'}
                            </button>
                          )}
                          <button className="btn btn-sm btn-outline" onClick={() => openEdit(p)}>
                            <Icon name="edit" size={13} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline"
                            disabled={isBusy}
                            onClick={() => toggleArchive(p)}
                          >
                            {isBusy ? '…' : p.status === 'active' ? 'Archive' : 'Restore'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <Modal
          title={modal.kind === 'create' ? 'New project' : 'Edit project'}
          onClose={() => setModal(null)}
          width={480}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" form="project-form" className="btn btn-primary" disabled={saving || !name.trim()}>
                {saving ? 'Saving…' : modal.kind === 'create' ? 'Create' : 'Save'}
              </button>
            </>
          }
        >
          <form id="project-form" className="stack-gap-sm" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                className="form-input"
                placeholder="e.g. Claims Portal"
                required
                autoFocus
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Domain</label>
              <input
                className="form-input"
                placeholder="e.g. Healthcare, E-commerce, Banking"
                value={domain}
                onChange={e => setDomain(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">About the project</label>
              <textarea
                className="form-input"
                rows={4}
                placeholder="What does this product do, and who uses it?"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
